import React, { useEffect, useState } from "react";
import DropDown from "../../../../components/common/dropdown";
import Spinner from "../../../../components/common/loader/components/spinner";
import { Collapse, Navbar, NavbarToggler } from "reactstrap";
import "../styles/style.css";
import { BOOKS_SORT_TYPES } from "../../../../common/constants";
import { useTranslation } from "react-i18next";
import { useSearchParams, useLocation } from "react-router";

const FilterBar = (props) => {
  const { t, i18n } = useTranslation();
  const languageClean = i18n.language.substring(0, 2);
  const [isOpen, setIsOpen] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const sortby = Object.keys(BOOKS_SORT_TYPES).map((key) => {
    return {
      name: t(key),
      id: key,
    };
  });

  function toggle() {
    setIsOpen(!isOpen);
  }

  useEffect(() => {
    const _searchParamsObject = Object.fromEntries([...searchParams]);

    // If we're visiting the page without parameters, clear existing Redux filters for a "fresh start"
    if (Object.keys(_searchParamsObject).length === 0) {
      props.changeSearchCriteria({}, props.stateHandler, props.actionType);
    }

    if (
      props.stateHandler === "literatureFilter" ||
      props.stateHandler === "audioFilter"
    ) {
      const initialCriteria = {
        sortby: "creationdate",
        language: languageClean.toUpperCase(),
        ..._searchParamsObject,
      };
      if (initialCriteria.language) {
        initialCriteria.language = initialCriteria.language.toUpperCase();
      }

      if (location.pathname.startsWith("/audio")) {
        initialCriteria.serieId = _searchParamsObject.serieId || "44";
      }
      props.changeSearchCriteria(
        initialCriteria,
        props.stateHandler,
        props.actionType
      );
      onSearchClicked();
    }
  }, [languageClean]);

  useEffect(() => {
    const areFiltersLoaded =
      props.bookSeries.length > 0 &&
      props.bookTopics.length > 0 &&
      props.bookLanguages.length > 0;
    if (!areFiltersLoaded) {
      props.getFilters();
    }
  }, [props.bookSeries, props.bookTopics, props.bookLanguages]);

  useEffect(() => {
    const _searchParamsObject = Object.fromEntries([...searchParams]);
    const searchParamsObject = {
      ..._searchParamsObject,
      language: _searchParamsObject?.language?.toUpperCase(),
    };
    const searchParamsKeyArray = Object.keys(searchParamsObject);

    if (searchParamsKeyArray.length > 0) {
      searchParamsKeyArray.map((key) => {
        if (searchParamsObject[key]) {
          props.changeSearchCriteria(
            { [key]: searchParamsObject[key] },
            props.stateHandler,
            props.actionType
          );
        }
      });
    }
    if (
      searchParamsObject?.reload == "true" &&
      props.literatureBooks.length > 0
    ) {
      props.changeSearchCriteria(
        { reload: "false" },
        props.stateHandler,
        props.actionType
      );
      onSearchClicked();
    }
  }, []);

  useEffect(() => {
    if (Object.keys(props.searchCriteria[props.stateHandler]).length > 0) {
      setSearchParams(props.searchCriteria[props.stateHandler], {
        replace: true,
      });
    }
  }, [props.searchCriteria[props.stateHandler]]);

  const onQueryInputClick = ({ target }, field) => {
    props.changeSearchCriteria(
      { [field]: target.value },
      props.stateHandler,
      props.actionType
    );
  };

  const getFilterControls = (filters) =>
    filters.map((filter) => {
      if (filter.source.length > 0) {
        const isFilterActive =
          props.searchCriteria[props.stateHandler][filter.field] &&
          props.searchCriteria[props.stateHandler][filter.field] != 0;

        var source = filter.skipSelect
          ? [...filter.source]
          : [{ id: null, name: t("all") }, ...filter.source];
        var filteredSource = source.filter(
          (r) => r.id == props.searchCriteria[props.stateHandler][filter.field]
        );

        var specifiedLabel =
          props.searchCriteria[props.stateHandler][filter.field] &&
          filteredSource.length > 0
            ? filteredSource[0].name
            : props.searchCriteria[props.stateHandler][filter.field];
        var _label =
          props.searchCriteria[props.stateHandler][filter.field] &&
          props.searchCriteria[props.stateHandler][filter.field] != 0
            ? specifiedLabel
            : filter.label;

        var labelShortened = _label.substring(0, 17);
        return (
          <div className="filter-item" key={filter.field}>
            <label>{t(filter.name)}</label>
            <DropDown
              className={isFilterActive ? "active-filter" : ""}
              enabled={true}
              items={source}
              label={labelShortened}
              onClick={(control) => onQueryInputClick(control, filter.field)}
              text={"name"}
              secondaryText={filter.field == "topicId" ? "booksCount" : null}
              value="id"
            />
          </div>
        );
      }
      return null;
    });

  const onSearchClicked = (e = null) => {
    e?.preventDefault();

    props.dispatchGet(() => {
      return props.searchFunction();
    });
    if (!isOpen) toggle();
  };

  const labelAll = t("all");
  const isAudioFilter = props.stateHandler === "audioFilter";
  const filters = [
    {
      name: "topics",
      field: "topicId",
      source: props.bookTopics,
      label: labelAll,
    },
    {
      name: "language",
      field: "language",
      source: props.bookLanguages,
      label: labelAll,
    },
    {
      name: "sortby",
      field: "sortby",
      source: sortby,
      skipSelect: true,
      label: sortby[0].name,
    },
  ];

  if (!isAudioFilter) {
    filters.splice(1, 0, {
      name: "series",
      field: "serieId",
      source: props.bookSeries,
      label: labelAll,
    });
  }

  var filterControls = getFilterControls(filters);

  const icon =
    props.bookSeries.length < 1 ? (
      <Spinner style={{ height: "16px", color: "white" }} />
    ) : (
      <i className="fa fa-search" />
    );

  const isSearchActive =
    props.searchCriteria[props.stateHandler].input?.length > 0;

  return (
    <div className="filters-wrapper">
      <Navbar expand="md" className="navbar-light">
        <NavbarToggler onClick={toggle} aria-label="Show or hide filters" />
        <Collapse isOpen={isOpen} navbar>
          <form onSubmit={onSearchClicked} className="filters-container">
            <div className="filter-item full-width-mobile">
              <label>{t("search")}</label>
              <input
                className={`form-control search-input ${
                  isSearchActive ? "active-filter" : ""
                }`}
                type="search"
                placeholder={t("search-placeholder")}
                value={props.searchCriteria[props.stateHandler].input || ""}
                aria-label="Search"
                readOnly={props.stateHandler == "mainFilter"}
                onChange={(control) => onQueryInputClick(control, "input")}
              />
            </div>

            {filterControls}

            <div className="search-button-container">
              <button
                type="submit"
                className="btn btn-primary btn-search"
                onClick={onSearchClicked}
              >
                {icon}
              </button>
            </div>
          </form>
        </Collapse>
      </Navbar>
    </div>
  );
};

export default FilterBar;
